import type { MeshGeometry } from './geometry'

type Point = readonly [number, number, number]

const subtract = (a: Point, b: Point): Point => [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
const cross = (a: Point, b: Point): Point => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]
const dot = (a: Point, b: Point): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
const unit = (value: Point): Point => {
  const length = Math.hypot(...value)
  return [value[0] / length, value[1] / length, value[2] / length]
}

/** Small faceted props have hard edges and deliberately independent face normals. */
class PropMesh {
  private readonly vertices: number[] = []
  private readonly indices: number[] = []

  triangle(a: Point, b: Point, c: Point, outward?: Point): void {
    let normal = cross(subtract(b, a), subtract(c, a))
    if (Math.hypot(...normal) < 1e-9) return
    if (outward && dot(normal, outward) < 0) {
      ;[b, c] = [c, b]
      normal = cross(subtract(b, a), subtract(c, a))
    }
    normal = unit(normal)
    for (const point of [a, b, c]) {
      this.indices.push(this.vertices.length / 8)
      this.vertices.push(...point, ...normal, point[0] + 0.5, point[2] + 0.5)
    }
  }

  polygon(points: readonly Point[], outward: Point): void {
    for (let i = 1; i < points.length - 1; i++) this.triangle(points[0], points[i], points[i + 1], outward)
  }

  finish(): MeshGeometry {
    return { vertices: new Float32Array(this.vertices), indices: new Uint32Array(this.indices) }
  }
}

/** Pointed bow toward -Z, tapered keel, crisp chine and a closed deck. */
function createHull(): MeshGeometry {
  const mesh = new PropMesh()
  const outline: readonly (readonly [number, number])[] = [
    [0, -0.5], [0.37, -0.31], [0.5, 0.1], [0.4, 0.44],
    [0.26, 0.5], [-0.26, 0.5], [-0.4, 0.44], [-0.5, 0.1], [-0.37, -0.31],
  ]
  const rings = [
    outline.map(([x, z]): Point => [x * 0.46, -0.5, z * 0.79]),
    outline.map(([x, z]): Point => [x * 0.86, -0.16, z * 0.94]),
    outline.map(([x, z]): Point => [x, 0.5, z]),
  ]
  mesh.polygon(rings[0], [0, -1, 0])
  mesh.polygon(rings[2], [0, 1, 0])
  for (let ring = 0; ring < rings.length - 1; ring++) {
    for (let i = 0; i < outline.length; i++) {
      const next = (i + 1) % outline.length
      const a = rings[ring][i]
      const b = rings[ring][next]
      mesh.polygon([a, b, rings[ring + 1][next], rings[ring + 1][i]], [a[0] + b[0], 0, a[2] + b[2]])
    }
  }
  return mesh.finish()
}

/** A sewn triangular sail, gently filled with wind; both sides are real faces. */
function createSail(): MeshGeometry {
  const mesh = new PropMesh()
  const segments = 8
  const point = (row: number, column: number): Point => {
    const b = row / segments
    const c = column / segments
    const a = 1 - b - c
    // Mast/foot stay straight; the center of the fabric bows away from the mast.
    return [Math.max(0, a * b * c) * 13.5 - 0.25, b - 0.5, c - 0.5]
  }
  for (let row = 0; row < segments; row++) {
    for (let column = 0; column < segments - row; column++) {
      const triangles: readonly (readonly [Point, Point, Point])[] = [
        [point(row, column), point(row + 1, column), point(row, column + 1)],
        ...(column < segments - row - 1
          ? [[point(row + 1, column), point(row + 1, column + 1), point(row, column + 1)] as const] : []),
      ]
      for (const [a, b, c] of triangles) {
        mesh.triangle(a, b, c, [1, 0, 0])
        mesh.triangle(a, b, c, [-1, 0, 0])
      }
    }
  }
  return mesh.finish()
}

/** A deterministic, asymmetric rock with broad readable facets. */
function createRock(): MeshGeometry {
  const mesh = new PropMesh()
  const count = 9
  const noise = (n: number): number => Math.sin(n * 17.17 + 3.71) * 0.5 + 0.5
  const rings: Point[][] = [
    [-0.5, 0.6], [-0.3, 0.99], [0.22, 0.88], [0.5, 0.34],
  ].map(([height, radius], row) => Array.from({ length: count }, (_, index): Point => {
    const angle = index / count * Math.PI * 2
    const r = radius * (0.83 + noise(index + row * 3.7) * 0.17) * 0.5
    return [Math.cos(angle) * r, height, Math.sin(angle) * r]
  }))
  // Normalize all axes so prop dimensions remain meaningful and bounds predictable.
  for (const axis of [0, 2] as const) {
    const values = rings.flat().map((point) => point[axis])
    const min = Math.min(...values)
    const max = Math.max(...values)
    for (const ring of rings) for (let i = 0; i < ring.length; i++) {
      const point = [...ring[i]] as [number, number, number]
      point[axis] = (point[axis] - min) / (max - min) - 0.5
      ring[i] = point
    }
  }
  mesh.polygon(rings[0], [0, -1, 0])
  mesh.polygon(rings[rings.length - 1], [0, 1, 0])
  for (let row = 0; row < rings.length - 1; row++) {
    for (let i = 0; i < count; i++) {
      const next = (i + 1) % count
      const a = rings[row][i]
      const b = rings[row][next]
      mesh.polygon([a, b, rings[row + 1][next], rings[row + 1][i]], [a[0] + b[0], 0, a[2] + b[2]])
    }
  }
  return mesh.finish()
}

/** Smooth low-poly sphere, used for foliage and satellite details. */
function createSphere(): MeshGeometry {
  const vertices: number[] = []
  const indices: number[] = []
  const columns = 20
  const rows = 12
  for (let row = 0; row <= rows; row++) {
    const latitude = row / rows * Math.PI
    for (let column = 0; column <= columns; column++) {
      const longitude = column / columns * Math.PI * 2
      const normal: Point = [Math.sin(latitude) * Math.cos(longitude), Math.cos(latitude), Math.sin(latitude) * Math.sin(longitude)]
      vertices.push(normal[0] * 0.5, normal[1] * 0.5, normal[2] * 0.5, ...normal, column / columns, row / rows)
    }
  }
  for (let row = 0; row < rows; row++) for (let column = 0; column < columns; column++) {
    const a = row * (columns + 1) + column
    const b = a + columns + 1
    if (row !== 0) indices.push(a, a + 1, b)
    if (row !== rows - 1) indices.push(a + 1, b + 1, b)
  }
  return { vertices: new Float32Array(vertices), indices: new Uint32Array(indices) }
}

/** Kept separate from game-piece geometry and created once per GPU device. */
export function createEnvironmentGeometry(shape: string): MeshGeometry | undefined {
  switch (shape) {
    case 'hull': return createHull()
    case 'sail': return createSail()
    case 'rock': return createRock()
    case 'sphere': return createSphere()
    default: return undefined
  }
}
